import React, { useState } from 'react';

import { MaterialIcon } from '@ninox/ninox-components';
import { Size } from '@ninox/ninox-components/theme';

import { translations } from '../../locale/translations';
import { SignUpProps } from '../Credentials';
import {
    Button,
    ErrorMessage,
    FormControl,
    GoogleButton,
    GoogleLogo,
    Input,
    Lines,
    Link,
    Separator,
    SwitchPage,
    Text
} from '../shared.styles';

export const SignIn = ({
    lang,
    formState,
    setFormState,
    mixPanelTracker,
    login,
    setUserId,
    googleTracker,
    googleLogin,
    afterLogin,
    canSignup,
    switchPage
}: SignUpProps) => {
    const [showErrors, setShowErrors] = useState(false);
    const [currentError, setCurrentError] = useState(false);
    const [password, setPassword] = useState('');
    const locale = translations[lang];
    const { email } = formState;
    const isValid = email.length && password.length;

    async function onLogin() {
        if (!isValid) {
            return setShowErrors(true);
        }

        await mixPanelTracker.track('SignIn', 'Click', { email });

        try {
            const { userId } = await login({ email, password });

            setUserId(userId);

            await googleTracker.trackLogin(userId);
            await mixPanelTracker.identify(userId);
            await mixPanelTracker.track('SignIn', 'Success', { email });

            afterLogin({ loginSuccessful: true });
        } catch (error) {
            await mixPanelTracker.track('SignIn', 'Error', { email });

            return setCurrentError(true);
        }
    }

    async function loginWithGoogle() {
        mixPanelTracker.track('SignInGoogle', 'Click', { email });

        try {
            const { userId, justSignedUp } = await googleLogin(false);

            setUserId(userId);
            await mixPanelTracker.track('SignInGoogle', justSignedUp ? 'SignedUp' : 'LoggedIn', {});

            if (justSignedUp) {
                await googleTracker.trackSignup(userId);
                await mixPanelTracker.identify(userId);
            } else {
                await googleTracker.trackLogin(userId);
                await mixPanelTracker.alias(userId);
            }

            afterLogin({ loginSuccessful: true });
        } catch (error) {
            await mixPanelTracker.track('SignInGoogle', 'Error', {});

            return setCurrentError(true);
        }
    }

    function handleEnter(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.nativeEvent.keyCode === 13 && isValid) {
            onLogin();
        }
    }

    return (
        <>
            <FormControl required>
                <label htmlFor="emailAddress">{locale.emailAddress}</label>
                <Input
                    aria-invalid={showErrors && email.length === 0}
                    autoCapitalize="off"
                    autoCorrect="off"
                    id="emailAddress"
                    required
                    spellCheck={false}
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setFormState({ email: e.target.value });
                        setCurrentError(false);
                    }}
                    onKeyDown={handleEnter}
                />
            </FormControl>
            <FormControl required>
                <label htmlFor="password">{locale.password}</label>
                <Input
                    aria-invalid={showErrors && password.length === 0}
                    id="password"
                    required
                    type="password"
                    value={password}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        setCurrentError(false);
                    }}
                    onKeyDown={handleEnter}
                />
            </FormControl>

            {currentError && (
                <ErrorMessage>
                    <MaterialIcon filling={0} icon="error" size={Size.xs} />
                    {locale.invalidCredentials}
                </ErrorMessage>
            )}

            <Button data-testid="login" emphasis="accent" size="m" onClick={onLogin}>
                {locale.login}
            </Button>

            {canSignup && (
                <>
                    <Separator>
                        <Lines>
                            <Text>{locale.op_or}</Text>
                        </Lines>
                    </Separator>

                    <GoogleButton icon={GoogleLogo} size="m" onClick={loginWithGoogle}>
                        {locale.loginWithGoogle}
                    </GoogleButton>

                    <Link data-testid="signup" onClick={() => switchPage('SignUp')}>
                        {locale.dontHaveAnAccount}
                    </Link>
                </>
            )}

            <SwitchPage>
                <span>{locale.problemsLoggingIn} </span>
                <Link data-testid="reset-password" onClick={() => switchPage('ForgotPassword')}>
                    {locale.resetPassword}
                </Link>
            </SwitchPage>
        </>
    );
};
